import { useMemo, useState } from "react";
import { Lock } from "lucide-react";
import { Badge } from "@baluu/ui/components/badge";
import { Button } from "@baluu/ui/components/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@baluu/ui/components/tabs";
import { CloudinaryUrl__TransformedUrl } from "@obby/lib";
import type { ViewContext } from "../types";
import { BookingApiError } from "../api";
import { BookingLoading, BookingProblem, Notice, RecordNotFound } from "../parts";
import { isOtherSchool } from "../school";
import { useClassroom } from "./manage/useClassroom";
import { deriveManageState, bundleDates } from "./manage/manageState";
import { currentOccurrence, isJoinable } from "./manage/runningStatus";
import { useRunningStatus } from "./manage/useRunningStatus";
import { ReceiptCard } from "./manage/ReceiptCard";
import { OrphanedBanner } from "./manage/OrphanedBanner";
import { GetReadyTab, PostClassTab } from "./manage/tabs";
import { PromptCard } from "./manage/parts";
import type { ClassroomPage } from "./manage/types";

// /bookings/manage/<id>: the customer's booking. Port of v1's
// BookingManageView + useBookingManageState + VirtualClassroom
// (apps/widget/src/components/booking-manage, components/VirtualClassroom):
// the receipt beside "Get ready" / "Post event notes", the banner when the
// school cancelled the session, and "Book again" once it has run. The page
// is reached by the booking id alone, like v1; only "Pay now" signs in.

export function ManageView({ ctx, bookingId }: { ctx: ViewContext; bookingId: string }) {
  const classroom = useClassroom(ctx.api, bookingId);

  if (classroom.isPending) return <BookingLoading rows={4} />;
  if (classroom.isError) {
    const notFound = classroom.error instanceof BookingApiError && classroom.error.status === 404;
    return notFound ? (
      <RecordNotFound />
    ) : (
      <BookingProblem title="Your booking couldn’t be loaded" message="Please try again in a moment." onRetry={() => void classroom.refetch()} />
    );
  }
  const { school, course, booking } = classroom.data;
  if (isOtherSchool(ctx.businessId, school, course?.schoolId, booking?.school)) return <RecordNotFound />;
  return <Classroom ctx={ctx} page={classroom.data} />;
}

/** Cloudinary-sized, and only an absolute address: the API's fallback
 * avatar is a path on v1's own host, which doesn't exist on the v2 site. */
function sizedImage(url: string | undefined, width: number, height: number): string | undefined {
  if (!url || !/^https?:\/\//.test(url)) return undefined;
  return CloudinaryUrl__TransformedUrl(url, { width, height, crop: "fill" }) as string;
}

function absolute(href: string): string {
  try {
    return new URL(href, window.location.href).toString();
  } catch {
    return href;
  }
}

function Classroom({ ctx, page }: { ctx: ViewContext; page: ClassroomPage }) {
  const state = deriveManageState(page);
  // The page's "now" for what doesn't tick (which date it is about, which
  // bundle dates are still to come), fixed when it opens, like v1.
  const [openedAt] = useState(() => Date.now());
  const occurrence = useMemo(() => currentOccurrence(page.session, openedAt), [page.session, openedAt]);
  const status = useRunningStatus(occurrence);
  const finished = status === "finished";
  // The tab the customer picked, for the status it was picked in: when the
  // class ends while the page is open, the notes tab unlocks and opens.
  const [picked, setPicked] = useState<{ tab: string; finished: boolean } | null>(null);
  const tab = picked && picked.finished === finished ? picked.tab : finished ? "postClass" : "getReady";
  const [refunded, setRefunded] = useState(false);

  const bookingId = page.bookingId || page.booking._id;
  const hostAvatar = sizedImage(page.teacherAvatar || page.defaultTeacherAvatar?.url, 140, 140);
  const hostName = page.teacherName ?? page.school?.name;
  const timezone = page.course?.timezone;

  const receipt =
    !finished || state.isBundle ? (
      <ReceiptCard
        ctx={ctx}
        page={page}
        state={state}
        occurrence={occurrence}
        now={openedAt}
        joinable={isJoinable(status)}
        image={sizedImage(page.classImage, 640, 360)}
        hostAvatar={hostAvatar}
        detailsUrl={absolute(ctx.links.manage(bookingId))}
      />
    ) : (
      <PromptCard
        title="Had a good time?"
        text="Come join us again—book your spot below!"
        action={
          <Button asChild className="font-semibold uppercase tracking-wide">
            <a href={ctx.links.event(page.classSlug)}>Book again</a>
          </Button>
        }
      />
    );

  return (
    <div className="space-y-8">
      <header>
        <span className="text-sm font-medium uppercase tracking-widest text-primary">Your booking receipt for:</span>
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <h1 className="text-3xl font-semibold leading-tight tracking-tight sm:text-4xl">{page.className}</h1>
          {state.isCancelled && (
            <Badge variant="destructive" className="text-xs uppercase tracking-wide">
              Cancelled
            </Badge>
          )}
        </div>
      </header>

      {refunded && <Notice tone="success">Refund requested — you’ll receive a confirmation email shortly.</Notice>}

      {state.showOrphanedBanner && (
        <OrphanedBanner
          api={ctx.api}
          bookingId={bookingId}
          rescheduleHref={state.canReschedule ? ctx.links.reschedule(bookingId) : null}
          canRefund={state.canRequestRefund}
          onRefunded={() => setRefunded(true)}
        />
      )}

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_380px] lg:gap-10">
        {/* On a phone the receipt comes first; beside the tabs on a wide screen. */}
        <aside className="lg:order-2">
          <div className="lg:sticky lg:top-24">{receipt}</div>
        </aside>

        <div className="min-w-0 lg:order-1">
          <Tabs value={tab} onValueChange={(next) => setPicked({ tab: next, finished })}>
            <TabsList className="w-full justify-start">
              <TabsTrigger value="getReady">Get ready</TabsTrigger>
              <TabsTrigger value="postClass" disabled={!finished}>
                Post event notes
                {!finished && <Lock className="ml-1.5 h-3.5 w-3.5 text-muted-foreground" aria-label="Opens after the event" />}
              </TabsTrigger>
            </TabsList>
            <TabsContent value="getReady">
              <GetReadyTab
                title="Hey, here is what you need to know before the event"
                notes={page.preClassNotes}
                hostName={hostName}
                hostAvatar={hostAvatar}
                whatYouWillLearn={page.whatYouWillLearn ?? []}
                whatYouWillNeed={page.whatYouWillNeed ?? []}
                bundleSessions={state.isBundle ? bundleDates(page.sessionsForBundle, { upcomingOnly: false, now: openedAt }) : []}
                timezone={timezone}
              />
            </TabsContent>
            <TabsContent value="postClass">
              <PostClassTab
                title="Hey, thanks for coming to the event!"
                notes={page.postClassNotes}
                hostName={hostName}
                hostAvatar={hostAvatar}
                reviewHref={state.reviewsEnabled ? ctx.links.review(bookingId) : null}
              />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
